import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Play } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1675726205553-4e348f24da2c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHw0fHxjaW5lbWF0aWMlMjBwb3J0cmFpdCUyMGRhcmslMjBsaWdodCUyMGRyYW1hdGljfGVufDB8fHx8MTc5MTM1NzM0OXww&ixlib=rb-4.1.0&q=85";

const Line = ({ children, delay = 0, active = true, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={active ? { y: 0 } : { y: "115%" }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ onNavigate, active = true }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 45, damping: 18 });
  const sy = useSpring(my, { stiffness: 45, damping: 18 });
  const imgX = useTransform(sx, (v) => v * -28);
  const imgY = useTransform(sy, (v) => v * -18);
  const coneX = useTransform(sx, (v) => v * 34);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      onMouseMove={onMove}
      className="relative min-h-screen flex items-end overflow-hidden"
    >
      <motion.div className="absolute inset-0" style={{ x: imgX, y: imgY, scale: 1.08 }}>
        <img
          src={HERO_IMG}
          alt="Cinematic portrait under dramatic light"
          className="w-full h-full object-cover opacity-45"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/55 to-ink/30" />
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 0%, rgba(232,226,213,0.14) 0%, rgba(8,8,10,0.6) 55%, #08080A 100%)",
        }}
      />
      <motion.div
        aria-hidden
        style={{ x: coneX }}
        className="absolute -top-10 left-1/2 -translate-x-1/2 w-[70vw] max-w-[820px] h-[85vh] animate-beam pointer-events-none"
      >
        <div
          className="w-full h-full"
          style={{
            clipPath: "polygon(46% 0, 54% 0, 88% 100%, 12% 100%)",
            background:
              "linear-gradient(180deg, rgba(247,244,238,0.22) 0%, rgba(229,192,123,0.05) 60%, transparent 100%)",
            filter: "blur(14px)",
          }}
        />
      </motion.div>

      <div className="relative z-10 w-full max-w-[1500px] mx-auto px-5 sm:px-10 pb-24 sm:pb-28 pt-40">
        <Line delay={0.15} active={active}>
          <span
            data-testid="hero-status-badge"
            className="inline-flex items-center gap-3 border border-ivory/15 bg-coal/60 backdrop-blur-md px-4 py-2 font-mono text-[10px] tracking-[0.3em] uppercase text-cream"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-amber animate-pulse" />
            Available for worldwide assignments 2026
          </span>
        </Line>

        <h1
          data-testid="hero-headline"
          className="font-display uppercase text-ivory mt-8 leading-[0.98] text-[13vw] sm:text-[9.5vw] lg:text-[7.5vw] tracking-[0.06em]"
        >
          <Line delay={0.3} active={active}>Ansh Agarwal</Line>
          <Line delay={0.45} active={active}>
            <span className="text-amber">Films</span>
          </Line>
        </h1>

        <Line delay={0.65} active={active} className="mt-6">
          <span className="font-serif italic font-light text-xl sm:text-3xl text-cream/90 tracking-wide">
            Painting with light &amp; time
          </span>
        </Line>

        <Line delay={0.8} active={active} className="mt-8">
          <span className="flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[10px] sm:text-[11px] tracking-[0.3em] uppercase text-mute">
            <span>Est. 2018</span>
            <span className="w-6 h-px bg-line" />
            <span>Mumbai · New Delhi</span>
            <span className="w-6 h-px bg-line" />
            <span>Photo + Cinema</span>
          </span>
        </Line>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={active ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
          transition={{ duration: 0.9, delay: 1, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-explore"
            onClick={() => onNavigate("#work")}
            className="group font-mono text-[11px] tracking-[0.3em] uppercase bg-ivory text-ink px-8 py-4 hover:bg-amber transition-colors duration-300"
          >
            Explore Work
          </button>
          <button
            data-testid="hero-cta-showreel"
            onClick={() => onNavigate("#films")}
            className="group flex items-center gap-3 font-mono text-[11px] tracking-[0.3em] uppercase text-ivory border border-ivory/25 px-8 py-4 hover:border-amber hover:text-amber transition-colors duration-300"
          >
            <Play size={13} className="fill-current" />
            Watch Showreel
          </button>
        </motion.div>
      </div>

      <div className="absolute bottom-8 right-6 sm:right-10 z-10 flex flex-col items-center gap-3">
        <span className="font-mono text-[9px] tracking-[0.4em] uppercase text-mute [writing-mode:vertical-rl]">
          Scroll
        </span>
        <div className="w-px h-16 bg-ivory/15 overflow-hidden">
          <div className="w-full h-full bg-amber animate-scroll-cue" />
        </div>
      </div>
    </section>
  );
};
